/**
 * Media URL helpers.
 *
 * Centralizes URL construction for preview images and face crops.
 * All paths are routed through the signed URL cache when auth is enabled.
 *
 * In local mode (auth disabled): /derivatives/{masterId}/preview.png (unsigned)
 * In auth mode: /derivatives/{masterId}/preview.png?X-PS-Signature=...&... (HMAC signed)
 * In cloud mode (future): CDN signed URLs (see docs/plans/CDN_Edge_Migration.md)
 */

import { getSignedUrl } from './signedUrlCache'

const CDN_BASE = import.meta.env.VITE_CDN_BASE_URL || import.meta.env.VITE_BFF_URL || ''

/**
 * Prepend CDN/BFF base to a relative signed path.
 * Signing always uses relative paths (/derivatives/...) so the BFF
 * can validate them. The base URL is prepended after signing for the
 * browser to fetch from the correct origin.
 */
function withBase(signedPath: string): string {
  if (!CDN_BASE || signedPath.startsWith('data:')) return signedPath
  return `${CDN_BASE}${signedPath}`
}

/**
 * Build signed preview image URL for a master_id.
 */
export function previewUrl(masterId: string): string {
  const path = `/derivatives/${masterId}/preview.png`
  return withBase(getSignedUrl(path))
}

/**
 * Build signed face crop URL from a face_id.
 * face_id format: {master_id}_face_{idx}
 */
export function faceCropUrl(faceId: string): string {
  const parts = faceId.split('_face_')
  if (parts.length === 2) {
    const [masterId, faceIdx] = parts
    const path = `/derivatives/${masterId}/faces/face_${faceIdx}.png`
    return withBase(getSignedUrl(path))
  }
  return ''
}

/**
 * Sign an arbitrary asset URL (e.g., crop_url from API responses).
 * Use this for URLs that come pre-built from the backend.
 */
export function signedSrc(url: string): string {
  return withBase(getSignedUrl(url))
}
